import {Css} from "nano-css/addon/vcssom/cssToTree";

export type ShowLinkedItemsMode = 'disabled' | 'all' | 'select';

export interface IGlobalStorefrontSettings {
    usePreview?: boolean;
    /** Storefront used when a flow needs one and the customer company doesn't name one
     * (e.g. the PSA quote-link quick flow seeding a prepared cart). */
    defaultPreparedCartStorefront?: string;
    bannerHeight?: number;
    useScrollingBanners?: boolean;
    name?: string;
    logo?: string;
    title?: string;
    bannerText?: string;
    theme?: string;
    menuBarColor?: string;
    menuBarTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
    headerFont?: string;
    defaultFont?: string;
    termsAndConditions?: string;
    orderEmail?: string;
    customerEmailText?: string;
    customerEmailSubject?: string;
    useCustomEmailTemplate?: boolean;
    customEmailTemplate?: string;
    storefrontsEnabled?: boolean;
    checkoutEnabled?: boolean;
    checkoutType?: string;
    defaultMarkup?: number;
    defaultMarkupType?: string;
    defaultShippingChargeType?: string;
    defaultShippingCharge?: number;
    defaultStorefrontSubUrl?: string;
    defaultStorefrontDomain?: string;
    currency: string;
    opportunityExtension?: string;
    paymentProcessor?: string;
    collectTax?: boolean;
    enableOpportunityCreation?: boolean;
    enableSalesOrderCreation?: boolean;
    orderFailureEmail?: string;
    storefrontVendors?: string[];
    vendorFilterStrategy?: string;
    requireNotes?: boolean;
    notesLabel?: string;
    notesInstructions?: string;
    locationLookupExtension?: string;
    locationLookupEnabled?: boolean;
    taxExtension?: string;
    customerLookupExtension?: string;
    storefrontBannerImage?: string;
    storefrontBannerLink?: string;
    storefrontBannerAltText?: string;
    defaultTaxCode?: string;
    footerContactDetails?: string;
    footerFacebook?: string;
    footerYoutube?: string;
    footerLinkedIn?: string;
    footerTwitter?: string;
    footerBluesky?: string;
    footerX?: string;
    footerPhone?: string;
    footerEmail?: string;
    footerLogo?: string;
    footerText?: string;
    headerLogo?: string;
    favicon?: string;
    headerLink?: string;
    useHeaderSearch?: boolean;
    includeFooter?: boolean;
    maxWidth?: number;
    showMfpOnProducts?: boolean;
    useCategories?: boolean;
    preparedCartEmailBody?: string;
    procurement?: IStorefrontProcurementSettings;
    showLinkedItemsMode?: ShowLinkedItemsMode;
}

export function buildGlobalSettingsPatch(
    original: IGlobalStorefrontSettings,
    current: IGlobalStorefrontSettings
): Partial<IGlobalStorefrontSettings> {
    const patch: Partial<IGlobalStorefrontSettings> = {};
    const keys = new Set([
        ...Object.keys(original),
        ...Object.keys(current),
    ]) as Set<keyof IGlobalStorefrontSettings>;

    for (const key of keys) {
        const origVal = original[key];
        const currVal = current[key];
        if (typeof currVal === 'object' || typeof origVal === 'object') {
            if (JSON.stringify(origVal) !== JSON.stringify(currVal)) {
                (patch as Record<string, unknown>)[key] = currVal;
            }
            continue;
        }
        // Treat undefined and '' as equivalent so clearing a never-set field isn't a diff
        const origNorm = origVal === undefined ? '' : origVal;
        const currNorm = currVal === undefined ? '' : currVal;
        if (origNorm !== currNorm) {
            (patch as Record<string, unknown>)[key] = currVal;
        }
    }
    return patch;
}

export function buildStorefrontPatch(
    original: IStorefrontSettings,
    current: IStorefrontSettings
): Partial<IStorefrontSettings> {
    const patch: Partial<IStorefrontSettings> = {};
    const keys = new Set([
        ...Object.keys(original),
        ...Object.keys(current),
    ]) as Set<keyof IStorefrontSettings>;

    for (const key of keys) {
        const origVal = original[key];
        const currVal = current[key];
        if (Array.isArray(currVal) || Array.isArray(origVal)) {
            if (JSON.stringify(origVal) !== JSON.stringify(currVal)) {
                (patch as Record<string, unknown>)[key] = currVal;
            }
            continue;
        }
        // Skip non-array objects — only diff primitives and arrays
        if (typeof currVal === 'object' && currVal !== null) continue;
        if (typeof origVal === 'object' && origVal !== null) continue;
        // Treat undefined and '' as equivalent so clearing a never-set field isn't a diff
        const origNorm = origVal === undefined ? '' : origVal;
        const currNorm = currVal === undefined ? '' : currVal;
        if (origNorm !== currNorm) {
            (patch as Record<string, unknown>)[key] = currVal;
        }
    }
    return patch;
}

export interface IStorefrontProcurementSettings {
    enableProcurementIntegration?: boolean;
    enableProcurementItemCreation?: boolean;
    vendors?: {[key: string]: IStorefrontProcurementVendorSettings};
}

export interface IStorefrontProcurementVendorSettings {
    enableOrderCreation?: boolean;
    autoApproveOrders?: boolean;
    autoApproveCostThreshold?: number;
    autoApproveItemCountThreshold?: number;
    procurementExtension?: string;
    dropShipToEndCustomer?: boolean;
    allowBackorder?: boolean;
}

export interface IStorefrontAdminSessionResponse {
    redirectUrl?: string;
}

export interface ICopyStorefrontRequest {
    name?: string;
    subUrl: string;
    sourceStorefrontId: string;
}

export interface ICopyStorefrontResponse {
    success?: boolean;
    message?: string;
    storefront?: IStorefrontSettings;
}

export interface IStorefrontTemplateGalleryItem {
    id?: string;
    url?: string;
    mediaType?: 'image' | 'video';
    altText?: string;
    sortOrder?: number;
    // The template-scoped palette style this image showcases. Unset = generic (always shown);
    // set = the picker's details view shows it only while that palette is selected.
    paletteId?: string;
}

export interface IStorefrontTemplateSettings {
    theme?: string;
    menuBarColor?: string;
    menuBarTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
    headerFont?: string;
    defaultFont?: string;
    customCSS?: string;
    logo?: string;
    headerLogo?: string;
    headerLink?: string;
    footerLogo?: string;
    favicon?: string;
    title?: string;
    bannerText?: string;
    maxWidth?: number;
    bannerHeight?: number;
    useScrollingBanners?: boolean;
    useHeaderSearch?: boolean;
    useCategories?: boolean;
    includeFooter?: boolean;
    footerContactDetails?: string;
    footerFacebook?: string;
    footerTwitter?: string;
    footerBluesky?: string;
    footerYoutube?: string;
    footerLinkedIn?: string;
    footerPhone?: string;
    footerEmail?: string;
    footerText?: string;
    sectionBackgroundColor?: string;
    sectionHeaderBackgroundColor?: string;
    sectionFooterBackgroundColor?: string;
    storefrontBannerImage?: string;
    storefrontBannerLink?: string;
    storefrontBannerAltText?: string;
    ignoreGlobalSections?: boolean;
    ignoreGlobalBanners?: boolean;
    ignoreGlobalLinks?: boolean;
    ignoreProductQuestions?: boolean;
    ignoreBundleQuestions?: boolean;
    // Styling page-config blobs (JSON strings) captured with the template so element styling —
    // including $styleRef references to bundled Storefront Styles — survives install.
    styleConfig?: string;
    headerConfig?: string;
    footerConfig?: string;
    headerLinksConfig?: string;
    notificationsConfig?: string;
    searchPageConfig?: string;
    homePageConfig?: string;
    cartPageConfig?: string;
    checkoutPageConfig?: string;
    productPageConfig?: string;
    bundlePageConfig?: string;
}

export interface IStorefrontTemplate {
    id?: string;
    name: string;
    description?: string;
    // Markdown long-form copy shown on the template details view in the picker.
    longDescription?: string;
    organization?: string;
    isSystem?: boolean;
    disabled?: boolean;
    gallery?: IStorefrontTemplateGalleryItem[];
    settings?: IStorefrontTemplateSettings;
    lastUpdated?: string;
    // The storefront this template was captured from. Only returned for templates owned by the
    // caller's organization — used to surface "this storefront is a template" affordances.
    sourceStorefrontId?: string;
    // Visibility filters for global (isSystem) templates. Only returned on own-org templates when
    // the caller holds the global templates permission + entitlement. Empty/undefined = no filter.
    organizations?: string[];
    productLevel?: string[];
    entitlements?: string[];
    // Style presets bundled with the template, returned by the single-template GET only. The picker
    // offers these as the starting palette / font pack; the API activates the cloned copies.
    palettes?: IStorefrontStyle[];
    fontPacks?: IStorefrontStyle[];
}

// Create a template from an existing storefront (POST storefront/storefronts/templates).
export interface ISaveStorefrontTemplateRequest {
    sourceStorefrontId: string;
    name: string;
    description?: string;
    isSystem?: boolean;
    // Visibility filters honored only when the caller may publish global templates.
    organizations?: string[];
    productLevel?: string[];
    entitlements?: string[];
}

// Provision a new storefront from a template (POST storefront/storefronts/from-template).
export interface ICreateStorefrontFromTemplateRequest {
    templateId: string;
    name: string;
    subUrl: string;
    domain?: string;
    // Ids of the TEMPLATE's bundled styles to start from. Omitted = keep whatever the template
    // captured as its defaults.
    paletteId?: string;
    fontPackId?: string;
}

export interface IStorefrontSettings {
    defaultCountry?: string;
    sharedCartEmailSubject?: string;
    sharedCartEmailBody?: string;
    disableOpportunityCreationQuoteRequest?: boolean;
    customerQuoteRequestEmailBody?: string;
    customerQuoteRequestEmailSubject?: string;
    salesRepQuoteRequestEmailBody?: string;
    salesRepQuoteRequestEmailSubject?: string;
    /* Comma-separated addresses blind-copied on the sales-rep quote-request notification. Storefront-level only. */
    quoteRequestBcc?: string;
    allowCartSharing?: boolean;
    allowRequestQuote?: boolean;
    defaultSalesRep?: string;
    opportunityExtension?: string;
    quoteRequestExtension?: string;
    taxExtension?: string;
    locationLookupExtension?: string;
    customerLookupExtension?: string;
    allowAnonymousAccess?: boolean;
    /** 'required' (or absent) = show the sign-in page; 'guest' = land shoppers on the home page as a
        guest. Only honored when allowAnonymousAccess is on. */
    loginMode?: string;
    /** 'magicLink' (or absent) | 'code' | 'poll' — how a shopper finishes a sign-in. */
    loginMethod?: string;
    /** 'allow' (or absent) | 'requireApproval' — what happens to a signup the CRM lookup can't resolve. */
    unresolvedSignupPolicy?: string;
    /** Comma-separated email domains that skip the approval gate. Whole-domain, case-insensitive. */
    autoApproveDomains?: string;
    /** JSON-encoded appearance of the sign-in / sign-up / token / confirm pages. */
    loginPageConfig?: string;
    anonymousShowPricing?: boolean;
    allowSignup?: boolean;
    includeFooter?: boolean;
    useHeaderSearch?: boolean;
    signupTermsAndConditions?: string;
    /** Comma separated staff addresses told about self-registrations. Blank = no signup emails. */
    signupNotificationEmail?: string;
    /** Staff email for an admitted signup. Blank uses the API's built-in default. */
    signupEmailSubject?: string;
    signupEmailBody?: string;
    /** Staff email for a signup held for approval. */
    signupApprovalEmailSubject?: string;
    signupApprovalEmailBody?: string;
    /** The email the SHOPPER gets when an admin approves them. Blank uses the API's default. */
    signupApprovedEmailSubject?: string;
    signupApprovedEmailBody?: string;
    notesMode?: string;
    notesLabel?: string;
    id?: string;
    name?: string;
    logo?: string;
    title?: string;
    theme?: string;
    bannerText?: string;
    menuBarColor?: string;
    menuBarTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
    headerFont?: string;
    defaultFont?: string;
    termsAndConditions?: string;
    overrideTermsAndConditions?: boolean;
    orderEmail?: string;
    customerEmailText?: string;
    customerEmailSubject?: string;
    useCustomEmailTemplate?: boolean;
    customEmailTemplate?: string;
    checkoutEnabled?: boolean;
    checkoutType?: string;
    defaultMarkup?: number;
    defaultMarkupType?: string;
    shippingChargeType?: string;
    shippingCharge?: number;
    fullCatalogs?: string[];
    subUrl: string;
    domain?: string;
    enabled?: boolean;
    currency?: string;
    paymentProcessor?: string;
    collectTax?: boolean;
    opportunitySettings?: IExtensionOverrideSetting[];
    quoteRequestOpportunitySettings?: IExtensionOverrideSetting[];
    disableOpportunityCreation?: boolean;
    disableSalesOrderCreation?: boolean;
    storefrontVendors?: string[];
    vendorFilterStrategy?: string;
    notesInstructions?: string;
    storefrontBannerImage?: string;
    storefrontBannerLink?: string;
    storefrontBannerAltText?: string;
    ignoreInheritedProducts?: boolean;
    // Product promos. Both are only honored while the org holds the ProductPromos entitlement: saves
    // force them back off without it, and the storefront-facing read gates them again, so a lapsed
    // entitlement takes effect without waiting for someone to re-save.
    enableVendorPromos?: boolean;
    enableMspPromos?: boolean;
    taxCode?: string;
    useScrollingBanners?: boolean;
    footerContactDetails?: string;
    footerFacebook?: string;
    footerYoutube?: string;
    footerLinkedIn?: string;
    footerTwitter?: string;
    footerBluesky?: string;
    footerX?: string;
    footerPhone?: string;
    footerEmail?: string;
    footerLogo?: string;
    footerText?: string;
    headerLogo?: string;
    headerLink?: string;
    favicon?: string;
    maxWidth?: number;
    bannerHeight?: number;
    ignoreGlobalLinks?: boolean;
    ignoreGlobalBanners?: boolean;
    ignoreGlobalSections?: boolean;
    sectionFooterBackgroundColor?: string;
    sectionBackgroundColor?: string;
    sectionHeaderBackgroundColor?: string;
    /* JSON-encoded page-presentation configs (v7.0). Header/footer are shared
       across all pages; search/home are page-specific. See Editor/searchPageConfig.ts.
       Additive: existing scalar style settings are unaffected. */
    headerConfig?: string;
    footerConfig?: string;
    searchPageConfig?: string;
    homePageConfig?: string;
    cartPageConfig?: string;
    checkoutPageConfig?: string;
    productPageConfig?: string;
    bundlePageConfig?: string;
    headerLinksConfig?: string;
    /* JSON-encoded notification bars: scheduled, dismissable bands above the header
       (see components/Storefront/Editor/notificationsConfig.ts). */
    notificationsConfig?: string;
    styleConfig?: string;
    /* READ-ONLY composed blob (v7.0) — the storefront's Storefront Styles plus the org's global
       styles, composed server-side at read time (the PagesConfig pattern). Never patched back: the
       admin editor edits styles through the dedicated /styles endpoints, the shopper renderer reads
       this. Shape: {"styles":[{id,name,elementType,scope,sortOrder,parts}]}.
       See components/Storefront/Editor/stylesConfig.ts. */
    stylesConfig?: string;
    /* JSON-encoded map of localization key -> replacement label (v7.0), letting a merchant
       override static shopper UI strings (e.g. per-language). Parse before use. */
    localizationConfig?: string;
    /* Product facet attributes configured as actionable on the storefront (shown on products,
       searchable, comparable). Capped by the org's storefrontAttributeLimit entitlement (default 20).
       An array, so it survives buildStorefrontPatch. */
    attributeSettings?: IStorefrontAttributeSetting[];
    /* When enabled, product cards rotate to a second product image on hover. */
    enableImageHoverSwap?: boolean;
    /* When enabled, products with video URLs show a play button that opens a video modal. */
    allowProductVideoPlayback?: boolean;
    /* When enabled, the product details page shows a gallery to scroll through all product images. */
    enableProductImageGallery?: boolean;
    /* When enabled, product tags are shown as colorful decorations on cards/rows and are filterable. */
    enableProductTags?: boolean;
    /* When enabled, signed-in shoppers can favorite products and filter search results to their favorites. */
    enableProductFavorites?: boolean;
    /* When enabled, product pages show a "frequently added together" recommendation row. Entitlement-gated:
       only editable by admins whose org has the shared ProductRecommendations entitlement (productRecommendationsAdmin permission). */
    enableFrequentlyAddedTogether?: boolean;
    /* When enabled, product pages show a "more like this" similar-products row. Entitlement-gated:
       only editable by admins whose org has the shared ProductRecommendations entitlement (productRecommendationsAdmin permission). */
    enableMoreLikeThis?: boolean;
    /* Master switch: whether search suggestions are BUILT for this storefront at all. Entitlement-gated,
       and what the nightly job keys on. Suggestions are built nightly, so a newly enabled storefront
       shows nothing until the next run (or a manual rebuild).

       Which search bars actually show a popout, which columns each shows, and how each popout is styled
       is per-bar config living in headerConfig / homePageConfig / searchPageConfig — see
       IAutocompleteConfig in components/Storefront/Editor/autocompleteConfig.ts. */
    enableSearchAutocomplete?: boolean;
    checkoutQuestionIds?: string[];
    quoteRequestQuestionIds?: string[];
    ignoreProductQuestions?: boolean;
    ignoreBundleQuestions?: boolean;
    useCategories?: boolean;
    preparedCartEmailBody?: string;
    preparedCartEmailSubject?: string;
    preparedCartCustomerCommentBody?: string;
    preparedCartCustomerCommentSubject?: string;
    allowPreviousAddresses?: boolean;
    isPreview?: boolean;
    hasPreview?: boolean;
    previewSubUrl?: string;
    liveSubUrl?: string;
    showLinkedItemsMode?: ShowLinkedItemsMode | null;
}
export interface IExtensionOverrideSetting {
    name?: string;
    value?: string
}

export interface IStorefrontAttributeSetting {
    key: string;          // product facet key, e.g. "Color"
    visible?: boolean;    // show on products
    searchable?: boolean; // filter/search by it
    comparable?: boolean; // include in product comparison
    sortOrder?: number;
}

// Distinct product facet attributes available across a storefront's products, for the attribute picker.
export interface IStorefrontAvailableAttribute {
    key: string;
    values: string[];
}

export interface IStorefrontAvailableAttributesResponse {
    attributes: IStorefrontAvailableAttribute[];
    attributeLimit: number; // org's configured attribute cap (default 20)
}

export interface IStorefrontSectionCopyRequest {
    existingId: string;
    storefront: string;
    newTitle?: string;
}

export interface ISectionToTemplateRequest {
    newTitle?: string;
    existingId?: string;
    storefront?: string;
    makePrivate?: boolean;
}

// Order-only change for an otherwise-unchanged section — sent in the delta's `sortOrders` list so
// the PATCH endpoint reorders without a full section payload.
export interface ISectionSortOrderUpdate {
    id: string;
    sortOrder: number;
}

// Delta payload for the PATCH sections endpoint. Unlike the legacy `sections/batch` (which deletes
// every section missing from the posted list), omission here NEVER deletes — deletes are explicit.
export interface IStorefrontSectionsDelta {
    upserts?: IStorefrontSection[];
    deletes?: string[];
    sortOrders?: ISectionSortOrderUpdate[];
}

export interface IStorefrontSection {
    title?: string;
    // Optional sub-header line rendered directly under the title, inside the same title band
    // (titleStyleProps background/padding wraps both). Follows the title's hideTitle treatment.
    subtitle?: string;
    sortOrder?: number;
    type?: string;
    // Layout width of the section on the storefront homepage:
    // 'quarter' | 'third' | 'half' | 'twoThirds' | 'threeQuarters' | 'full'.
    // Absent/null is treated as 'full' (the historical single-column behavior). The homepage
    // preview row-packs consecutive fractional sections (quarter=25%, third=33.33%, half=50%,
    // twoThirds=66.67%, threeQuarters=75%) onto one row.
    width?: 'quarter' | 'third' | 'half' | 'twoThirds' | 'threeQuarters' | 'full';
    // When true, the shopper does NOT render the section's header/title row at all (and thus no
    // collapse control). The title stays populated — it's still the admin display name used in
    // pickers. The editor still renders the header, dimmed, so sections stay identifiable.
    hideTitle?: boolean;
    // Whether shoppers can collapse this section by clicking its header.
    //
    // Absent/true is the historical behavior — every titled section is collapsible — so existing
    // storefronts are unchanged. False removes the +/- control AND the header's click/keyboard
    // handlers, and the section always renders expanded: a merchant who wants a hero row that can
    // never be folded away shouldn't have to rely on shoppers not trying.
    //
    // Moot when `hideTitle` is set: with no header there is nothing to click and nothing to draw the
    // control on, so such a section is already permanently expanded.
    collapsible?: boolean;
    // When true, the section's styleProps.backgroundColor extends edge-to-edge (a 100vw band) on the
    // shopper. When fractional-width sections are packed into one row and ANY member has this flag,
    // the WHOLE row is wrapped in a single full-bleed band colored by the FIRST such member's
    // styleProps.backgroundColor.
    fullWidthBackground?: boolean;
    // Vertical alignment within a shared fractional row: 'top' | 'center' | 'bottom' (absent = stretch).
    verticalAlign?: 'top' | 'center' | 'bottom';
    // The Custom Page this section belongs to (an IStorefrontPage.id). Absent/empty means the main
    // homepage. See components/Storefront/Editor/pagesConfig.ts.
    page?: string;
    // Opaque JSON config for content-typed sections (video/richText/banner) that have
    // no products. Stored/returned verbatim by the API (never parsed server-side) — same pattern as
    // overlayConfig/homePageConfig. See components/Storefront/Editor/Sections/contentSectionConfig.ts.
    contentConfig?: string;
    savedSearchId?: string;
    manufacturers?: string[];
    subCategories?: string[];
    categories?: string[];
    segments?: string[];
    tags?: string[];
    id?: string;
    disabled?: boolean;
    // When true, the section's products render as a horizontal side-scroller on the storefront
    // instead of the default wrap grid.
    scrollProducts?: boolean;
    storefront?: string;
    shared?: boolean;
    productIds?: string[];
    maxEntries?: number;
    audience?: string[];
    styleProps?: Css;
    titleStyleProps?: Css;
    headerStyleProps?: Css;
    footerStyleProps?: Css;
    /**
     * Per-section overrides for styling that otherwise lives on the PAGE, keyed by the `homePageConfig`
     * key each entry overrides (`title`, `price`, `addToCartButton`, `sectionTitleText`, …).
     *
     * A section's five style bags only ever covered its container. Everything painting inside it — the
     * card name, the price plate, the add-to-cart button, the side-scroll chrome, the knocked-out block
     * behind the title — came from one `homePageConfig` shared by every section on the page, so two
     * sections could not differ below the band. This is that missing layer (AC-1165).
     *
     * Resolution is `{ ...homePageConfig[key], ...styleOverrides[key] }`, per section. Absent or empty
     * therefore renders exactly as it did before this existed.
     */
    styleOverrides?: { [configKey: string]: Css };
    productStyleProps?: Css;
    headerCells?: IStorefrontSectionCell[]
    footerCells?: IStorefrontSectionCell[]
    // Tuning for a type === 'interest' (Recommended For You) section. Title, maxEntries and
    // scrollProducts are reused from the common fields above.
    interestConfig?: IStorefrontInterestConfig;
    /** Curated promo section (selectedPromos) config. Absent on every other section type. */
    promoConfig?: IStorefrontPromoConfig;
    isNew?: boolean; // Used on the frontend to identify sections that haven't been saved yet, so they can be given temporary IDs and default values as needed. Not stored in the backend.
}

// Well-known interest signal sources. cartRemoved is a negative signal (demotes rejected items);
// othersPurchased pulls in what other shoppers of this storefront have bought.
export type InterestSignalSource =
    | 'favorited'
    | 'viewed'
    | 'viewedNotCarted'
    | 'purchased'
    | 'cartAdded'
    | 'cartRemoved'
    | 'othersPurchased'
    | 'similarToRecentlyViewed';

// One enabled signal in an interest section. weight is an optional per-rule multiplier; when unset
// the BFF applies a sensible per-source default.
export interface IInterestSignalRule {
    source: InterestSignalSource;
    weight?: number;
}

/**
 * Which campaigns and which distributors feed a curated promo section.
 *
 * Both lists are opt-in and start empty, and an empty configuration renders NOTHING rather than
 * everything — a half-configured row showing the whole catalogue's promotions would be a worse failure
 * than an empty one, and the automatic "On Promotion" section already means everything live.
 */
export interface IStorefrontPromoConfig {
    /** Campaign ids. Only ones live for this storefront resolve; naming another changes nothing. */
    promotionIds?: string[];
    /** Distributors whose promotional costs feed the row. Empty means no vendor promos, not all. */
    vendors?: string[];
}

export interface IStorefrontInterestConfig {
    // Maximum products to show in the row. Falls back to a BFF default when unset.
    maxItems?: number;
    // Recency half-life in days for the time-decay term.
    recencyHalfLifeDays?: number;
    // When true, drop items the shopper has already purchased.
    excludeAlreadyPurchased?: boolean;
    // The signals the MSP has chosen to feed this row. Empty/undefined = no signals = no products.
    signals?: IInterestSignalRule[];
}

export interface IStorefrontSectionCell {
    type?: string;
    title?: string;
    text?: string;
    sourceUrl?: string;
    linkUrl?: string;
    altText?: string;
    styleProps?: Css
}

export interface IStorefrontBanner {
    backgroundColor?: string;
    name?: string;
    linkUrl?: string;
    imageUrl?: string;
    altText?: string;
    id?: string;
    sortOrder?: number;
    storefront?: string;
    shared?: boolean;
    disabled?: boolean;
    // The Custom Page this banner belongs to (an IStorefrontPage.id). Absent/empty means the main
    // homepage. See components/Storefront/Editor/pagesConfig.ts.
    page?: string;
    // Overlay text (title / subtitle / CTA button) as one opaque JSON string —
    // see components/Storefront/Editor/bannerOverlayConfig.ts for the shape.
    overlayConfig?: string;
}

// A Custom Page: an additional homepage served at {domain}/{slug}. Each has its own banners and
// sections (referenced by the page's stable `id` via their `page` field). Persisted as a first-class
// API resource (storefront/storefronts/{storefront}/pages), preview-resolved server-side. The hero
// presentation fields mirror IHeroBannerOptions (see components/Storefront/Editor/pagesConfig.ts).
export interface IStorefrontPage {
    // Stable page GUID; sections/banners reference this via their `page` field. Client may generate
    // on create (genPageId in pagesConfig.ts). Never changes once created — the slug is the mutable,
    // human-facing part.
    id?: string;
    // Page type — 'home' for now; absent = 'home'.
    type?: string;
    // URL path segment ({domain}/{slug}). Lowercase, hyphen-separated, unique, not reserved.
    slug: string;
    title?: string;
    // Absent = enabled; only explicit false disables (the shopper still stores the page + content).
    enabled?: boolean;
    sortOrder?: number;
    // Optional per-page banner height (px). Falls back to the storefront/global banner height.
    bannerHeight?: number;
    // Hero banner presentation (mirrors IHeroBannerOptions).
    bannerFullWidth?: boolean;
    bannerImageFit?: 'fit' | 'fill';
    bannerUnderHeader?: boolean;
    bannerUnderHeaderBackground?: string;
    bannerUnderHeaderShadow?: string;
}

export interface IStorefrontQuestion {
    id?: string;
    identifier?: string;
    questionText?: string;
    answers?: string[];
    category?: string;
    answerType?: string;
    isRequired?: boolean;
    entityType?: string;
    entityId?: string;
    default?: boolean;
    allowMultiSelect?: boolean;
}

// A "general" opportunity token available to the storefront opportunity extension override fields; mirrors the
// API's OpportunityTokenDefinition (served by GET storefront/settings/opportunityTokens/{context}).
/**
 * Token catalogs the API can serve, mirroring OpportunityTokenCatalog.ForContext. Each names one
 * server-side token builder, so a context is really "which event's tokens".
 */
export type TokenContext = 'order' | 'quoteRequest' | 'signup' | 'signupApproved' | 'extensionError';

/** The built-in subject/body a configurable storefront email falls back to. Served by the API so the
 *  editor can offer it as a starting point without keeping a second copy that would drift. */
export interface IEmailTemplateDefaults {
    key?: string;
    /** Null when the default subject is composed at send time, so there is nothing to pre-fill. */
    subject?: string;
    body?: string;
}

export interface IOpportunityTokenDefinition {
    key?: string;
    description?: string;
}

export interface IStorefrontLink {
    url?: string;
    text?: string;
    isSeparator?: boolean;
    id?: string;
    sortOrder?: number;
    storefront?: string;
    shared: boolean;
    disabled?: boolean;
    type: string;
    // Optional per-entry text color (any CSS color) applied to this menu link.
    color?: string;
}

export interface IProductCategory {
    id: string;
    identifier: string;
    segmentId?: string;
    label?: string;
    smallImageUrl?: string;
    largeImageUrl?: string;
    description?: string;
    displayPriority?: number;
}

export interface IProductCategoryMapping {
    id: string;
    category?: string;
    segment: string;
    externalCategoryId?: string;
    externalSegmentId?: string;
    externalProductTypeId?: string;
    context: string;
}

export interface IProductSegment {
    id?: string;
    identifier?: string;
    label?: string;
    smallImageUrl?: string;
    largeImageUrl?: string;
    description?: string;
    displayPriority?: number;
}

export interface IStorefrontOrder {
    id: string;
    storefront: string;
    emailAddress: string;
    ipAddress: string;
    orderNumber: number;
    status: string;
    createDate: Date;
    total: number;
    subtotal: number;
    // Nullable server-side: an order that was never quoted or paid carries no tax/shipping at all.
    shipping?: number;
    tax?: number;
    // "STRIPE" / "NONE" — which processor took the payment (and therefore where tax came from).
    paymentMethod?: string;
    shippingAddress: IStorefrontOrderAddress;
    billingAddress: IStorefrontOrderAddress;
    products: IStorefrontOrderProduct[];
    bundles: IStorefrontOrderBundle[];
    costTotal: number;
    questions?: IStorefrontOrderQuestionAnswer[];
    presentedTerms?: string;
    workspaceId?: string;
    preparedCartId?: string;
    shipments?: IOrderShipment[];
}

export interface IOrderShipment {
    shipmentCarrier?: string;
    shipmentTrackingNumber?: string;
    shipmentTrackingUrl?: string;
    shipmentTrackingId?: string;
    price?: number;
    eta?: string;
    updates?: string[];
    weight?: number;
}

export interface IStorefrontOrderQuestionAnswer {
    entityType: string;
    entityId: string;
    questionText: string;
    answer: string;
    identifier?: string;
}

export interface IStorefrontOrderActivity {
    id: string;
    timestamp: Date;
    orderId?: string;
    orderNumber?: number;
    activityType?: string;
    user?: string;
    message?: string;
    success?: boolean;
    error?: string;
    changedFields?: string[];
    references?: IStorefrontOrderActivityReference[];
}

export interface IStorefrontOrderActivityReference {
    referenceType: string;
    referenceId: string;
}

export interface IStorefrontOrderProduct {
    productId: string;
    quantity: number;
    price?: number;
    name?: string;
    manufacturer?: string;
}

export interface IStorefrontOrderBundle {
    bundleId: string;
    id: string;
    quantity: number;
    price?: number;
    cost?: number;
    name?: string;
    image?: string;
    products: IStorefrontOrderBundleProduct[];
}

export interface IStorefrontOrderBundleProduct {
    productId: string;
    quantity: number;
    name?: string;
    image?: string;
    cost: IStorefrontCost;
}

export interface IStorefrontCost {
    cost: number;
    distributor?: string;
}

export interface IStorefrontOrderAddress {
    name?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    phoneNumber?: string;
}

export interface IStorefrontTheme {
    name: string;
    menuBarColor?: string;
    menuBarTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
}

export interface IStorefrontCart {
    id: string;
    storefront: string;
    emailAddress: string;
    createDate: Date;
    lastVisit: Date;
    lastUpdate: Date;
    productCount: number;
    bundleCount: number;
    products: IStorefrontCartProduct[];
    bundles: IStorefrontCartBundle[];
}

export interface IStorefrontCartProduct {
    productId: string;
    name?: string;
    quantity: number;
}

export interface IStorefrontCartBundle {
    bundleId: string;
    name?: string;
    quantity: number;
}

export interface IStorefrontMonthlyOrders {
    year: number;
    month: number;
    count: number;
    total: number;
}

export interface IPaymentProcessorSetupDetails {
    stripeSetup?: boolean;
    stripeCreateInvoice?: boolean;
    stripeInvoiceDescription?: string;
    stripeInvoiceFooter?: string;
    stripeCollectPhoneNumber?: boolean;
}

export interface IStripeSetupRequest {
    key: string;
}

export interface IStripeConfigRequest {
    createInvoice: boolean;
    invoiceDescription?: string;
    invoiceFooter?: string;
    collectPhoneNumber: boolean;
}

export interface IStripeValidationResponse {
    success: boolean;
    url?: string;
    error?: string;
}

export interface ISmtpSettings {
    enabled?: boolean;
    host?: string;
    port?: number;
    useTls?: boolean;
    username?: string;
    configured?: boolean;
    fromLogin?: string;
    fromLoginName?: string;
    fromOrder?: string;
    fromOrderName?: string;
    fromQuote?: string;
    fromQuoteName?: string;
    fromPreparedCart?: string;
    fromPreparedCartName?: string;
}

export interface ISmtpSetupRequest {
    enabled?: boolean;
    host?: string;
    port?: number;
    useTls?: boolean;
    username?: string;
    password?: string;
    fromLogin?: string;
    fromLoginName?: string;
    fromOrder?: string;
    fromOrderName?: string;
    fromQuote?: string;
    fromQuoteName?: string;
    fromPreparedCart?: string;
    fromPreparedCartName?: string;
}

export interface ISmtpTestRequest {
    recipient: string;
}

export interface ISmtpTestResponse {
    success: boolean;
    error?: string;
}

export interface IEmailActivity {
    id?: string;
    timestamp: Date;
    emailType?: string;
    to?: string;
    from?: string;
    subject?: string;
    status?: string;
    provider?: string;
    error?: string;
    serverResponse?: string;
    referenceId?: string;
}

export const PAYMENT_PROCESSOR_KEYS = {
    NONE: 'NONE',
    STRIPE: 'STRIPE',
}

export const PAYMENT_PROCESSOR_DISPLAY = {
    'NONE': 'None',
    'STRIPE': 'Stripe',
}

export interface IStorefrontOrderStatuses {
    [key: string]: string;
}

export interface IStorefrontCustomTheme {
    id?: string;
    name: string;
    menuBarColor?: string;
    menuBarTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
    headerFont?: string;
    defaultFont?: string;
    customCSS?: string;
}

export interface IStorefrontBundle {
    id: string;
    name: string;
    description?: string;
    image?: string;
    products: IStorefrontBundleProduct[];
    price?: number;
    maxQuantity?: number;
}

export interface IStorefrontBundleEstimate {
    maxQuantity?: number;
    cost?: number;
    price?: number;
    messages?: {[key:string]: string};
}

// Per-item cost/price breakdown for a bundle (server-calculated). UnitPrice is the single-unit sell
// price with markup applied; Price is the item's price for its per-bundle quantity.
export interface IBundleItemPrice {
    productId?: string;
    cost?: number;
    price?: number;
    unitPrice?: number;
}

export interface IPreparedCartDirectLinksResponse{
    links: IPreparedCartDirectLink[];
}

export interface IPreparedCartDirectLink {
    link: string;
    emailAddress: string;
}

export interface IStorefrontBundleProduct {
    id: string;
    name: string;
    description?: string;
    image?: string;
    quantity?: number;
    maxQuantity?: number;
    cost?: IStorefrontCost;
}

export interface IStorefrontOrderReference {
    storefrontId: string;
    orderId: string;
    orderNumber?: number;
}
// ─── Storefront Styles ────────────────────────────────────────────────────────
// Named, reusable element style presets ("Storefront Styles"). A merchant defines e.g. a
// *Primary* button style once and every element that references it (via the reserved
// `$styleRef` key inside that element's style dict) picks up the preset underneath its own
// local overrides. See components/Storefront/Editor/stylesConfig.ts for the resolution
// semantics (the hand-mirror of the shopper renderer's Helpers/stylesConfig.ts).

// The five preset types are element style presets; `palette` and `fontPack` are the token docs
// (a named colour palette / font pairing) that style VALUES anywhere may reference through a
// `$palette:`/`$font:` token. See PALETTE_SLOTS / FONT_SLOTS in stylesConfig.ts.
export type StorefrontStyleElementType = 'button' | 'card' | 'section' | 'typography' | 'form' | 'palette' | 'fontPack';

// A style's content: part name -> camelCase CSS dictionary (the ISearchElementStyle shape).
// Parts per element type are renderer-defined conventions — see STYLE_PARTS in stylesConfig.ts.
// A `palette` doc instead carries { colors: { slot -> colour } } and a `fontPack` doc
// { fonts: { heading, body } }, which is the same "part -> dictionary" shape.
export interface IStorefrontStyleParts {
    [part: string]: { [cssProp: string]: any };
}

export interface IStorefrontStyle {
    id: string;
    name: string;
    elementType: StorefrontStyleElementType;
    // Scope: the owning storefront id, `global` (org-wide), `{storefrontId}-preview` (staged) or a
    // template id (bundled). Same convention as sections. `global` renders a scope badge in the panel.
    storefront: string;
    sortOrder: number;
    /**
     * The active doc of its element type for this scope. Only meaningful on `palette` / `fontPack`,
     * where at most one doc per type is active at a time and the activate endpoint clears every
     * sibling. Read strictly as `=== true` so a stray truthy value can never activate a doc.
     */
    isActive?: boolean;
    parts: IStorefrontStyleParts;
    lastUpdated?: string;
}

// POST/PUT body. `id` absent = create.
export type IStorefrontStyleSaveRequest = Omit<IStorefrontStyle, 'id' | 'lastUpdated'> & { id?: string };

// ─── Storefront Assets ────────────────────────────────────────────────────────
// Every image a storefront uses (logos, favicons, banners, section/style imagery) registered as a
// first-class document so it can be named, tagged, reused across storefronts, replaced in place and
// audited for usage. Scope travels on `storefront` exactly like sections/styles: a storefront id, or
// `global` for an org-wide asset every storefront may reference.

// Scope value for org-wide assets (the `storefront` field, and the create-time scope).
export const STOREFRONT_ASSET_GLOBAL = 'global';

// How the asset got into the library: uploaded through the UI, discovered by the storage scan of
// pre-existing blobs, or brought in with a storefront template.
export type StorefrontAssetSource = 'upload' | 'scan' | 'template';

// Where a usage reference was found. `settings`/`globalSettings` are the storefront/global settings
// documents; the rest are the corresponding storefront documents.
export type StorefrontAssetUsageLocation = 'settings' | 'globalSettings' | 'banner' | 'section' | 'style' | 'template';

export interface IStorefrontAsset {
    id: string;
    // Owning storefront id, or `global`.
    storefront: string;
    name: string;
    tags: string[];
    // Public URL: what gets written into settings/section/style documents.
    url: string;
    // Storage-relative path; the identity used by the scan/orphan-cleanup flow.
    blobPath: string;
    contentType: string;
    sizeBytes: number;
    source: StorefrontAssetSource;
    username?: string;
    lastUpdated?: string;
}

// One place the asset's URL was found. Delete is blocked while any usage exists.
export interface IStorefrontAssetUsage {
    location: StorefrontAssetUsageLocation;
    documentId: string;
    documentName?: string;
    storefront?: string;
    // Property on the document holding the URL (e.g. `headerLogo`).
    field?: string;
}

// What an activity entry records. Every asset mutation stamps one, so the panel's Activity tab is
// the audit trail for a single asset. `registered` is the storage scan adopting a pre-existing blob.
export type StorefrontAssetActivityType =
    'uploaded' | 'renamed' | 'retagged' | 'replaced' | 'promoted' | 'deleted' | 'registered';

// One recorded action against an asset. Same wrapper/shape convention as IStorefrontOrderActivity.
export interface IStorefrontAssetActivity {
    id?: string;
    timestamp: string;
    assetId?: string;
    activityType?: StorefrontAssetActivityType | string;
    user?: string;
    message?: string;
}

// A blob in storage that no registered asset points at, a candidate for cleanup.
export interface IOrphanBlob {
    blobPath: string;
    url: string;
    sizeBytes: number;
    lastModified?: string;
}

export interface IStorefrontAssetScanResult {
    // Blobs newly registered as assets by this scan.
    registered: number;
    // Blobs that were already registered.
    alreadyRegistered: number;
    // URLs referenced by documents that don't resolve to a blob we own (external/legacy hosting).
    legacyUrls: number;
    orphans: IOrphanBlob[];
}

// Upload/replace response. `url` is always present; `asset` is the registered document (absent only
// if a server ever returns the bare URL), so callers should prefer `asset.url` and fall back.
export interface IStorefrontAssetUploadResponse {
    url: string;
    asset?: IStorefrontAsset;
}

// Whether a storefront's draft is currently held by somebody else's edit lease. Mirrors the API's
// V_7.0 StorefrontEditLeaseStatus. The assistant holds the lease for the length of one restyle turn;
// while it does, the storefront write endpoints answer a human's save with a 409, so the editors poll
// this and go read-only instead of letting the user work into a rejection.
export interface IStorefrontEditLeaseStatus {
    locked: boolean;
    // The holder's token (the assistant session id) when locked.
    holder?: string;
    heldByAssistant: boolean;
}

// ── Storefront Users ────────────────────────────────────────────────────────────────────
// A view over contacts, computed by the API: a person's access to a storefront can come from their
// own contact record, their company, the organization default, or an entry added on the Users
// screen. `accessSource` says which, because that is what the screen exists to answer.

/** Values of IStorefrontUser.accessSource. Mirrors StorefrontUser's constants on the API. */
export const ACCESS_DIRECT = 'Direct';
export const ACCESS_ADDITIONAL = 'Added here';
export const ACCESS_COMPANY = 'Via company';
export const ACCESS_DEFAULT = 'Default storefront';
export const ACCESS_PENDING = 'Pending approval';
export const ACCESS_DENIED = 'Denied';

export interface IStorefrontUser {
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    companyName?: string;
    accessSource?: string;
    disabled?: boolean;
    contactId?: string;
    companyId?: string;
    /** True only when access came from this screen, and so can be taken away from it. */
    revocable: boolean;
    /** 'pending' | 'approved' | 'denied', or absent when the contact was never gated. */
    approvalStatus?: string;
    /** Waiting on an admin. Listed despite having no access yet — the queue lives here. */
    pending: boolean;
}

export interface IStorefrontUsersResponse {
    items?: IStorefrontUser[];
    total?: number;
    /** Set when the list was truncated, so a short list is never mistaken for the whole membership. */
    notice?: string;
}

/**
 * Whether a storefront was built by the AI design assistant, and whether a session is working on it
 * right now. Mirrors the API's StorefrontAiGenerationStatus.
 *
 * A separate read from the storefront list because only stores that have had an assistant session
 * have a row at all, so most orgs get an empty list back.
 */
export interface IStorefrontAiGeneration {
    storefrontId?: string;
    /** An assistant generation session created this storefront. Permanent. */
    generated: boolean;
    /** Present only while that session is genuinely still running; the API reconciles before answering. */
    activeSessionId?: string;
    /** 'building' | 'awaitingReview' while a session is live, otherwise absent. */
    activeStatus?: string;
    /** The model the most recent session ran on. */
    lastModelKey?: string;
    /** building | awaitingReview | published | cancelled | failed | completed. */
    lastOutcome?: string;
    sessionCount?: number;
}

export interface IStorefrontUserAccessResponse {
    success: boolean;
    message?: string;
    updated: number;
    skipped?: { contactId: string, reason: string }[];
}

/**
 * A starter set of products and bundles for a storefront with nothing on it yet.
 *
 * Mirrors the API's StorefrontProductSuggestions. Purely a proposal: the caller stages it, edits it, and the
 * existing bulk listing call is what actually writes.
 */
export interface IStorefrontProductSuggestions {
    products: IStorefrontSuggestedProduct[];
    bundles: IStorefrontSuggestedBundle[];
    /** The catalog the ids belong to, and the catalog segment of the bulk listing route used to apply them. */
    catalog?: string;
    /** The markup a product inherits if it is listed without an override: the storefront's, falling back to global. */
    defaultMarkup?: number;
    /** The code for that markup, not its friendly name: FIXED, PERCENT or GROSS_MARGIN. */
    defaultMarkupType?: string;
}

export interface IStorefrontSuggestedProduct {
    id: string;
    name?: string;
    manufacturer?: string;
    mpn?: string;
    imageUrl?: string;
    /** Why it was suggested. See STOREFRONT_SUGGESTION_SOURCE. */
    source: string;
    inStock: boolean;
    /**
     * The best vendor cost under the storefront's effective vendor filters, or absent when no vendor passes
     * them. A product can be in stock and still have no cost here.
     */
    cost?: number;
    /** The vendor that cost came from. */
    costVendor?: string;
    /** What the storefront would charge for it, cost plus the markup it would inherit. */
    price?: number;
    /** The product is priced by quote, so cost and price are deliberately absent rather than unknown. */
    hidePrice?: boolean;
    /**
     * How many of the organization's OTHER storefronts already list this product, this one excluded. Sent for
     * any source when the API knows it, so a purchase-history row can also carry it.
     */
    storefrontCount?: number;
    /** The product's catalog category, set for any source when known. The commonCategory chip names it. */
    category?: string;
}

export interface IStorefrontSuggestedBundle {
    id: string;
    name?: string;
    productCount: number;
    imageUrl?: string;
    /**
     * Why it was suggested. Bundles only ever carry two of the sources: onStorefronts when at least one of the
     * organization's other storefronts already lists the bundle, and catalog when none of them do.
     */
    source: string;
    /** How many of the organization's OTHER storefronts already list this bundle, this one excluded. */
    storefrontCount?: number;
    /** What the bundle's products cost the organization, when every one of them has a cost. */
    cost?: number;
    /** What this storefront would charge for the bundle under its own pricing. */
    price?: number;
    /** Every product in the bundle is available, so the bundle itself can be bought. */
    inStock: boolean;
    /**
     * Why the storefront could not put a price on this bundle, naming the failing part numbers. Set only when
     * the pricing pass ran and this specific bundle failed it; absent when the bundle priced fine or when
     * pricing as a whole was unavailable.
     */
    unpricedReason?: string;
}

/** Mirrors the API's StorefrontSuggestionSources. */
export const STOREFRONT_SUGGESTION_SOURCE = {
    PURCHASE_HISTORY: 'purchaseHistory',
    ON_STOREFRONTS: 'onStorefronts',
    BOUGHT_TOGETHER: 'boughtTogether',
    COMMON_CATEGORY: 'commonCategory',
    IN_STOCK: 'inStock',
    /** Bundles only: an active bundle in the catalog that no other storefront carries. */
    CATALOG: 'catalog',
} as const;
